import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import FloatingEmergencyButton from "./FloatingEmergencyButton";
import { AccountPreferencesPanel } from "./settings/AccountPreferencesPanel";
import { ProfileSettingsPanel } from "./settings/ProfileSettingsPanel";
import { notificationsApi } from "../lib/api/handlers/notifications";
import type { NotificationRecord } from "../lib/api/types";
import { ApiError } from "../lib/api/http";
import { PATHS, buildViewPath } from "../routes/paths";
import UlendoWordmark from "./branding/UlendoWordmark";
import { Wifi, WifiOff, RefreshCcw, Settings, Download } from "lucide-react";
import Swal from "sweetalert2";
import { useOfflineMode } from "../features/offline/state/OfflineModeProvider";
import { useOfflineCacheStatus } from "../features/offline/state/OfflineCacheStatusContext";
import { syncPendingQueue, CONNECTIVITY_RESTORED_EVENT, OFFLINE_SYNC_COMPLETED_EVENT } from "../lib/offline/sync";
import { SyncIssuesModal } from "../features/offline/components/SyncIssuesModal";
import { listSyncIssues, type SyncIssueEntry } from "../lib/offline/db";
import { useQueuedSubmissionCount } from "../hooks/useQueuedSubmissions";
import {
  BACKEND_FAILURE_EVENT,
  BACKEND_RECOVERY_EVENT,
  isBackendHealthy,
} from "../lib/system/backendHealth";

type NavbarProps = {
  onEmergencyAlert?: () => void;
  onExportData?: () => void;
  onSignOut?: () => void;
  chatWidget?: React.ReactNode;
  user?: {
    name: string;
    role: string;
    avatar?: string;
    email?: string;
    phone?: string;
    dutyStation?: string;
  };
};

type BeforeInstallPromptEvent = Event & {
  readonly platforms?: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
};

function isStandaloneDisplayMode() {
  if (typeof window === "undefined") return false;
  const mediaQueryMatch = typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
  const navigatorStandalone = typeof navigator !== "undefined" && (navigator as unknown as { standalone?: boolean }).standalone;
  return Boolean(mediaQueryMatch || navigatorStandalone);
}

function formatRelativeTime(timestamp: string) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSeconds < 60) return `${Math.max(diffSeconds, 0)}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  if (diffSeconds < 604800) return `${Math.floor(diffSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function severityAccent(severity?: string, read?: boolean) {
  if (read) return "border-transparent";
  const normalized = typeof severity === "string" ? severity.toLowerCase() : "";
  switch (normalized) {
    case "critical":
      return "border-red-500";
    case "warning":
      return "border-amber-400";
    case "success":
      return "border-emerald-500";
    default:
      return "border-[var(--ulendo-forest)]";
  }
}

function formatDetailedDate(timestamp?: string | null) {
  if (!timestamp) return "Not available";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString();
}

export default function Navbar({
  onEmergencyAlert,
  onExportData,
  onSignOut,
  chatWidget,
  user,
}: NavbarProps) {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showAccountPreferences, setShowAccountPreferences] = useState(false);
  const [showSyncIssues, setShowSyncIssues] = useState(false);
  const {
    enabled: offlineModeEnabled,
    toggle: toggleOfflineMode,
    setEnabled: setOfflineModeEnabled,
    lastUpdated: offlineModeLastUpdated,
  } = useOfflineMode();
  const offlineCacheStatus = useOfflineCacheStatus();
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const [isBackendAvailable, setIsBackendAvailable] = useState(() => isBackendHealthy());
  const queuedCount = useQueuedSubmissionCount();
  const [syncingNow, setSyncingNow] = useState(false);
  const [syncIssues, setSyncIssues] = useState<Array<SyncIssueEntry & { id: number }>>([]);
  const syncingRef = React.useRef(false);
  const autoSyncPromptRef = React.useRef(false);
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [appInstalled, setAppInstalled] = useState(() => isStandaloneDisplayMode());
  const showInstallButton = Boolean(!appInstalled && installPromptEvent && !installDismissed);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);

  const refreshSyncIssues = useCallback(async () => {
    if (typeof window === "undefined") return;
    const issues = await listSyncIssues();
    setSyncIssues(issues);
  }, []);

  const handleManualSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncingNow(true);
    try {
      const { processed, failed, errors, issues } = await syncPendingQueue();
      const hasErrors = errors.length > 0;
      await Swal.fire({
        icon: failed > 0 ? "warning" : "success",
        title:
          failed > 0 || issues > 0
            ? "Sync completed with issues"
            : processed > 0
              ? "Sync completed"
              : "No queued submissions",
        text: hasErrors
          ? errors[0]
          : processed > 0 || failed > 0 || issues > 0
            ? `${processed} processed, ${failed} pending, ${issues} needs review.`
            : "No queued submissions to sync.",
      });
      await refreshSyncIssues();
    } finally {
      syncingRef.current = false;
      setSyncingNow(false);
    }
  }, [refreshSyncIssues]);

  const handleAppInstall = useCallback(async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    try {
      const choice = await installPromptEvent.userChoice;
      if (choice?.outcome === "dismissed") {
        setInstallDismissed(true);
      } else {
        setAppInstalled(true);
      }
    } catch {
      setInstallDismissed(true);
    } finally {
      setInstallPromptEvent(null);
    }
  }, [installPromptEvent]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleStatus = () => {
      setIsOnline(typeof navigator === "undefined" ? true : navigator.onLine);
    };
    window.addEventListener("online", handleStatus);
    window.addEventListener("offline", handleStatus);
    return () => {
      window.removeEventListener("online", handleStatus);
      window.removeEventListener("offline", handleStatus);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
      setInstallDismissed(false);
    };
    const handleAppInstalled = () => {
      setInstallPromptEvent(null);
      setInstallDismissed(false);
      setAppInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt as EventListener);
    window.addEventListener("appinstalled", handleAppInstalled as EventListener);
    const mediaQuery = typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;
    const handleDisplayModeChange = () => setAppInstalled(isStandaloneDisplayMode());
    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", handleDisplayModeChange);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(handleDisplayModeChange);
      }
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt as EventListener);
      window.removeEventListener("appinstalled", handleAppInstalled as EventListener);
      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === "function") {
          mediaQuery.removeEventListener("change", handleDisplayModeChange);
        } else if (typeof mediaQuery.removeListener === "function") {
          mediaQuery.removeListener(handleDisplayModeChange);
        }
      }
    };
  }, []);

  const prevOnlineRef = React.useRef(isOnline);
  const offlinePromptRef = React.useRef(false);
  const backendDownPromptRef = React.useRef(false);
  const showConnectivityPrompt = useCallback(async () => {
    if (autoSyncPromptRef.current) return;
    if (queuedCount <= 0) return;
    autoSyncPromptRef.current = true;
    try {
      const result = await Swal.fire({
        icon: "info",
        title: "Connectivity restored",
        text: "Offline records are ready to sync. Would you like to sync them now?",
        showCancelButton: true,
        confirmButtonText: "Sync now",
        cancelButtonText: "Later",
      });
      if (result.isConfirmed) {
        await handleManualSync();
      }
    } finally {
      autoSyncPromptRef.current = false;
    }
  }, [queuedCount, handleManualSync]);

  const promptOfflineActivation = useCallback(async () => {
    if (offlineModeEnabled || offlinePromptRef.current) {
      return;
    }
    offlinePromptRef.current = true;
    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "Connection lost",
        text: "It looks like the internet connection just dropped. Enable offline mode to keep capturing data and sync once you're back online?",
        showCancelButton: true,
        confirmButtonText: "Enable offline mode",
        cancelButtonText: "Not now",
      });
      if (result.isConfirmed) {
        setOfflineModeEnabled(true);
        await Swal.fire({
          icon: "success",
          title: "Offline mode enabled",
          text: "We'll keep your work queued safely until the connection returns.",
        });
      }
    } finally {
      offlinePromptRef.current = false;
    }
  }, [offlineModeEnabled, setOfflineModeEnabled]);

  useEffect(() => {
    if (!isOnline && prevOnlineRef.current) {
      void promptOfflineActivation();
    } else if (isOnline && !prevOnlineRef.current) {
      void showConnectivityPrompt();
    }
    prevOnlineRef.current = isOnline;
  }, [isOnline, promptOfflineActivation, showConnectivityPrompt]);

  useEffect(() => {
    if (!showSettingsMenu) return undefined;
    const handleClickAway = (event: MouseEvent) => {
      if (!settingsMenuRef.current) return;
      const target = event.target as Node | null;
      if (target && settingsMenuRef.current.contains(target)) return;
      setShowSettingsMenu(false);
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, [showSettingsMenu]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = () => {
      void showConnectivityPrompt();
    };
    window.addEventListener(CONNECTIVITY_RESTORED_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(CONNECTIVITY_RESTORED_EVENT, handler as EventListener);
    };
  }, [showConnectivityPrompt]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleBackendFailure = (_event: Event) => {
      setIsBackendAvailable(false);
      if (!isOnline || offlineModeEnabled || backendDownPromptRef.current) return;
      backendDownPromptRef.current = true;
      void Swal.fire({
        icon: "warning",
        title: "Services unavailable",
        text: "We can't reach the server right now. Switch to offline mode to keep working? Anything you capture will stay queued and sync automatically once services recover.",
        showCancelButton: true,
        confirmButtonText: "Enable offline mode",
        cancelButtonText: "Stay online",
      })
        .then((result) => {
          if (result.isConfirmed) {
            setOfflineModeEnabled(true);
          }
        })
        .finally(() => {
          backendDownPromptRef.current = false;
        });
    };
    window.addEventListener(BACKEND_FAILURE_EVENT, handleBackendFailure);
    const handleBackendRecovery = () => {
      setIsBackendAvailable(true);
    };
    window.addEventListener(BACKEND_RECOVERY_EVENT, handleBackendRecovery);
    return () => {
      window.removeEventListener(BACKEND_FAILURE_EVENT, handleBackendFailure);
      window.removeEventListener(BACKEND_RECOVERY_EVENT, handleBackendRecovery);
    };
  }, [isOnline, offlineModeEnabled, setOfflineModeEnabled]);

  useEffect(() => {
    void refreshSyncIssues();
  }, [refreshSyncIssues]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleSyncComplete = (
      event: Event,
    ) => {
      const detail = (event as CustomEvent<{ processed: number; failed: number; errors: string[]; issues?: number }>).detail;
      if (!detail || (!detail.processed && !detail.failed && !(detail.issues && detail.issues > 0))) {
        return;
      }
      const toastIcon = detail.failed > 0 || (detail.issues ?? 0) > 0 ? "warning" : "success";
      const title =
        detail.failed > 0 || (detail.issues ?? 0) > 0
          ? `${detail.processed} processed, ${detail.failed} pending`
          : `${detail.processed} offline record${detail.processed === 1 ? "" : "s"} synced`;
      const text = detail.errors && detail.errors.length
        ? detail.errors[0]
        : detail.failed > 0 || (detail.issues ?? 0) > 0
          ? "Some submissions need review. Open sync issues for details."
          : "Offline data is up to date.";
      void refreshSyncIssues();
      void Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: toastIcon,
        title,
        text,
        timer: 4000,
        showConfirmButton: false,
      });
    };
    window.addEventListener(OFFLINE_SYNC_COMPLETED_EVENT, handleSyncComplete as EventListener);
    return () => {
      window.removeEventListener(OFFLINE_SYNC_COMPLETED_EVENT, handleSyncComplete as EventListener);
    };
  }, [refreshSyncIssues]);

  const apiAvailable = isOnline && isBackendAvailable;

  const offlineStatusText = useMemo(() => {
    if (!offlineModeEnabled) {
      return apiAvailable ? "Disabled" : "Reconnect to sync";
    }
    if (offlineCacheStatus.state === "prefetching") {
      if (offlineCacheStatus.total > 0) {
        const step = Math.min(offlineCacheStatus.completed + 1, offlineCacheStatus.total);
        return `Caching ${step}/${offlineCacheStatus.total}`;
      }
      return "Caching…";
    }
    if (offlineCacheStatus.state === "error") {
      return "Caching failed";
    }
    if (offlineCacheStatus.state === "success") {
      return apiAvailable ? "Ready" : "Captured";
    }
    return apiAvailable ? "Ready" : "Capturing offline";
  }, [apiAvailable, offlineCacheStatus, offlineModeEnabled]);

  const offlineLastChanged = offlineModeLastUpdated
    ? new Date(offlineModeLastUpdated).toLocaleString()
    : "Not set yet";

  const offlineTitle = useMemo(() => {
    if (!offlineModeEnabled) {
      return `Offline mode disabled. Click to enable manual offline capture. Last changed ${offlineLastChanged}.`;
    }
    if (offlineCacheStatus.state === "prefetching") {
      const progress =
        offlineCacheStatus.total > 0
          ? `${Math.min(offlineCacheStatus.completed + 1, offlineCacheStatus.total)} of ${offlineCacheStatus.total}`
          : "in progress";
      return `Offline mode enabled. Caching offline resources (${progress}). Stay online until this completes. Last changed ${offlineLastChanged}.`;
    }
    if (offlineCacheStatus.state === "error") {
      return `Offline mode enabled but caching failed. Stay online and retry. Last changed ${offlineLastChanged}.`;
    }
    return `Offline mode enabled. ${
      apiAvailable
        ? "Online now – queued when network drops."
        : "Currently collecting offline – will sync on reconnect."
    } Last changed ${offlineLastChanged}.`;
  }, [apiAvailable, offlineCacheStatus, offlineLastChanged, offlineModeEnabled]);

  const pointOfEntry = user?.dutyStation?.trim() || "Port of Entry";
  const [notificationItems, setNotificationItems] = useState<NotificationRecord[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const hasHydratedNotificationsRef = useRef(false);
  const prevUnreadRef = useRef(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const showOfflineGuidance = !apiAvailable && !offlineModeEnabled;
  const cacheProgressPercent =
    offlineCacheStatus.state === "prefetching" && offlineCacheStatus.total > 0
      ? Math.round((offlineCacheStatus.completed / offlineCacheStatus.total) * 100)
      : 0;
  const lastOfflineChangeRelative = offlineModeLastUpdated
    ? formatRelativeTime(new Date(offlineModeLastUpdated).toISOString())
    : null;
  const syncStatusMessage = syncingNow
    ? "Syncing offline queue"
    : queuedCount > 0
      ? `${queuedCount} draft${queuedCount === 1 ? "" : "s"} queued`
      : apiAvailable
        ? "All drafts synced"
        : offlineModeEnabled
          ? "Queued until online"
          : "Offline capture disabled";
  const isOfflineExperience = !apiAvailable || offlineModeEnabled;
  const connectivityBadgeClasses = isOfflineExperience
    ? "bg-rose-50 text-rose-700"
    : "bg-emerald-50 text-emerald-700";

  const fetchNotifications = React.useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const page = await notificationsApi.list({ page: 0, size: 10 });
      const items = page?.content ?? [];
      setNotificationItems(items);
      const unreadCount = items.filter((n) => !n.readAt).length;
      const previousUnread = prevUnreadRef.current;
      if (hasHydratedNotificationsRef.current && unreadCount > previousUnread) {
        void playNotificationSound();
      }
      prevUnreadRef.current = unreadCount;
      hasHydratedNotificationsRef.current = true;
      setNotificationsError(null);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Unable to load notifications.";
      setNotificationsError(message);
      setNotificationItems([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  const handleMarkAllAsRead = React.useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      const timestamp = new Date().toISOString();
      setNotificationItems((prev) =>
        prev.map((notification) => ({ ...notification, readAt: timestamp })),
      );
      prevUnreadRef.current = 0;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Could not update notifications.";
      setNotificationsError(message);
    }
  }, []);

  const handleNotificationClick = React.useCallback(
    async (notification: NotificationRecord) => {
      if (!notification.readAt) {
        try {
          await notificationsApi.markAsRead(notification.id);
          const timestamp = new Date().toISOString();
          setNotificationItems((prev) =>
            prev.map((item) => (item.id === notification.id ? { ...item, readAt: timestamp } : item)),
          );
        } catch (error) {
          const message = error instanceof ApiError ? error.message : "Unable to update notification.";
          setNotificationsError(message);
        }
      }
      const referencePath =
        notification.referenceType === "WATER_QUALITY_ALERT" && notification.referenceId
          ? buildViewPath(PATHS.PREMISES_WATER_QUALITY_CREATE, notification.referenceId)
          : null;

      const createdBy = notification.createdBy?.trim() || "System";
      const createdAt = formatDetailedDate(notification.createdAt ?? notification.deliveredAt);
      const detailsHtml = `
        <div class="text-left space-y-3 text-[var(--ulendo-text-muted)]">
          ${
            notification.message
              ? `<p class="text-sm leading-relaxed">${notification.message}</p>`
              : "<p class=\"text-sm leading-relaxed\">No additional message.</p>"
          }
          <dl class="text-sm">
            <div class="flex items-start gap-2">
              <dt class="w-28 font-semibold text-[var(--ulendo-forest-dark)]">Created by</dt>
              <dd class="flex-1">${createdBy}</dd>
            </div>
            <div class="mt-2 flex items-start gap-2">
              <dt class="w-28 font-semibold text-[var(--ulendo-forest-dark)]">Date created</dt>
              <dd class="flex-1">${createdAt}</dd>
            </div>
          </dl>
        </div>
      `;

      const result = await Swal.fire({
        title: notification.title || "Notification",
        html: detailsHtml,
        confirmButtonText: referencePath ? "Open details" : "Close",
        showCancelButton: Boolean(referencePath),
        cancelButtonText: "Close",
        focusConfirm: false,
      });

      if (result.isConfirmed && referencePath) {
        navigate(referencePath);
        setShowNotifications(false);
      }
    },
    [navigate],
  );

  const unreadCount = useMemo(
    () => notificationItems.filter((n) => !n.readAt).length,
    [notificationItems],
  );

  const playNotificationSound = useCallback(async () => {
    try {
      const ContextClass = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!ContextClass) return;
      if (!audioContextRef.current || audioContextRef.current.state === "closed") {
        audioContextRef.current = new ContextClass();
      }
      if (audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume().catch(() => undefined);
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 880;
      gain.gain.value = 0.08;
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.18);
    } catch {
      // Ignore sound playback failures (e.g., autoplay restrictions)
    }
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-[rgba(14,91,42,0.16)] bg-[var(--ulendo-haze)]/95 px-6 py-3 shadow-[0_4px_18px_rgba(15,90,42,0.08)] backdrop-blur-md">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <UlendoWordmark size={44} />
            <div className="hidden md:block leading-tight">
              <p className="text-xs uppercase tracking-[0.32em] text-[var(--ulendo-text-muted)]">Port Health</p>
              <h1 className="text-lg font-semibold text-[var(--ulendo-forest)]">{pointOfEntry}</h1>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <span
            className={`inline-flex items-center gap-2 rounded-full border border-[rgba(14,91,42,0.14)] bg-white/90 px-4 py-2 text-sm font-semibold text-[var(--ulendo-forest-dark)] ${connectivityBadgeClasses}`}
          >
            {isOfflineExperience ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
            {isOfflineExperience ? "Offline" : "Online"}
          </span>

          <div className="flex items-center justify-end gap-3">
            {onExportData ? (
              <button
                onClick={onExportData}
                className="hidden rounded-xl border border-[rgba(14,91,42,0.16)] bg-white/90 px-3 py-2 text-sm font-semibold text-[var(--ulendo-forest-dark)] transition hover:border-[rgba(14,91,42,0.28)] hover:text-[var(--ulendo-forest)] md:inline-flex"
              >
                Export Data
              </button>
            ) : null}
            {showInstallButton ? (
              <button
                type="button"
                onClick={handleAppInstall}
                className="inline-flex items-center gap-2 rounded-xl border border-[rgba(14,91,42,0.16)] bg-gradient-to-r from-emerald-50/70 to-white/90 px-3 py-2 text-sm font-semibold text-[var(--ulendo-forest-dark)] transition hover:border-[rgba(14,91,42,0.3)] hover:text-[var(--ulendo-forest)]"
                title="Install the PHMIS app on this device"
              >
                <Download className="h-4 w-4" />
                Install app
              </button>
            ) : null}
            <div className="relative" ref={settingsMenuRef}>
              <button
                onClick={() => {
                  setShowSettingsMenu((prev) => !prev);
                  setShowNotifications(false);
                  setShowUserMenu(false);
                }}
                className={`relative rounded-xl border border-[rgba(14,91,42,0.16)] bg-white/90 p-2.5 text-[var(--ulendo-text-muted)] transition hover:border-[rgba(14,91,42,0.28)] hover:text-[var(--ulendo-forest-dark)] ${
                  showSettingsMenu ? "shadow-md" : ""
                }`}
                aria-label="System controls"
              >
                <Settings className="h-5 w-5" />
                {queuedCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[var(--ulendo-forest)] px-1 text-[10px] font-semibold text-white shadow">
                    {queuedCount > 9 ? "9+" : queuedCount}
                  </span>
                )}
              </button>
              {showSettingsMenu && (
                <div className="absolute left-1/2 mt-3 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-[rgba(14,91,42,0.16)] bg-white/95 py-2 shadow-xl">
                  <div className="border-b border-[rgba(14,91,42,0.12)] px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide text-[var(--ulendo-text-muted)]">Connection</p>
                    <p className="text-sm font-semibold text-[var(--ulendo-forest-dark)]">{syncStatusMessage}</p>
                    <p className="text-xs text-[var(--ulendo-text-muted)]">
                      {offlineStatusText}
                      {lastOfflineChangeRelative ? ` · updated ${lastOfflineChangeRelative}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleOfflineMode()}
                    aria-pressed={offlineModeEnabled}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[var(--ulendo-forest-dark)] transition hover:bg-[var(--ulendo-haze)]"
                    title={offlineTitle}
                  >
                    <div>
                      <p className="text-sm font-semibold">Offline mode</p>
                      <p className="text-xs text-[var(--ulendo-text-muted)]">
                        {offlineModeEnabled ? "Enabled for capture" : "Disabled"}
                      </p>
                    </div>
                    <span
                      className={`relative inline-flex h-5 w-10 items-center rounded-full border transition ${
                        offlineModeEnabled ? "border-indigo-400 bg-indigo-500/80" : "border-slate-300 bg-white"
                      }`}
                    >
                      <span
                        className={`absolute h-4 w-4 rounded-full bg-white shadow transition ${
                          offlineModeEnabled ? "translate-x-5" : "translate-x-1"
                        }`}
                      />
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowSettingsMenu(false);
                      await handleManualSync();
                    }}
                    disabled={syncingNow}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[var(--ulendo-forest-dark)] transition hover:bg-[var(--ulendo-haze)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--ulendo-haze)] text-[var(--ulendo-forest-dark)]">
                        <RefreshCcw className={`h-4 w-4 ${syncingNow ? "animate-spin" : ""}`} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold">{syncingNow ? "Syncing…" : "Sync queue"}</p>
                        <p className="text-xs text-[var(--ulendo-text-muted)]">
                          {queuedCount > 0 ? `${queuedCount} draft${queuedCount === 1 ? "" : "s"} pending` : "Queue clear"}
                        </p>
                      </div>
                    </div>
                    {queuedCount > 0 ? (
                      <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[var(--ulendo-forest)] px-1.5 py-0.5 text-xs font-semibold text-white">
                        {queuedCount}
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSettingsMenu(false);
                      setShowSyncIssues(true);
                    }}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[var(--ulendo-forest-dark)] transition hover:bg-[var(--ulendo-haze)]"
                  >
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--ulendo-haze)] text-[var(--ulendo-forest-dark)]">
                        <RefreshCcw className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Sync issues</p>
                        <p className="text-xs text-[var(--ulendo-text-muted)]">
                          {syncIssues.length > 0
                            ? `${syncIssues.length} record${syncIssues.length === 1 ? "" : "s"} need review`
                            : "No conflicts detected"}
                        </p>
                      </div>
                    </div>
                    {syncIssues.length > 0 ? (
                      <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                        {syncIssues.length}
                      </span>
                    ) : null}
                  </button>
                </div>
              )}
            </div>
            <FloatingEmergencyButton variant="navbar" onClick={onEmergencyAlert} />

          <div className="relative">
            <button
              onClick={() => {
                setShowNotifications((prev) => !prev);
                setShowSettingsMenu(false);
              }}
              className="relative rounded-xl border border-[rgba(14,91,42,0.16)] bg-white/90 p-2.5 text-[var(--ulendo-text-muted)] transition hover:border-[rgba(14,91,42,0.28)] hover:text-[var(--ulendo-forest-dark)]"
              aria-label="Notifications"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--ulendo-gold)] text-xs font-semibold text-[var(--ulendo-forest-dark)] shadow-sm">
                  {unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute left-1/2 mt-3 w-80 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-[rgba(14,91,42,0.16)] bg-white/95 p-2 shadow-xl">
                <div className="flex items-center justify-between rounded-xl bg-[var(--ulendo-haze)]/90 px-3 py-2">
                  <h3 className="text-sm font-semibold text-[var(--ulendo-forest-dark)]">Notifications</h3>
                  <button
                    className="text-xs font-semibold uppercase tracking-wide text-[var(--ulendo-text-muted)] hover:text-[var(--ulendo-forest)] disabled:opacity-60"
                    onClick={handleMarkAllAsRead}
                    disabled={notificationsLoading || notificationItems.length === 0}
                  >
                    Mark all read
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto py-2">
                  {notificationsLoading ? (
                    <div className="px-4 py-6 text-center text-[var(--ulendo-text-muted)]">Loading notifications…</div>
                  ) : notificationsError ? (
                    <div className="px-4 py-6 text-center text-red-500">{notificationsError}</div>
                  ) : notificationItems.length > 0 ? (
                    notificationItems.map((notification) => {
                      const severityLabel = notification.severity?.replace("_", " ").toLowerCase();
                      const unread = !notification.readAt;
                      const borderClass = severityAccent(notification.severity ?? undefined, !!notification.readAt);
                      return (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full rounded-xl border ${borderClass} bg-[var(--ulendo-haze)]/70 px-4 py-3 text-left transition hover:bg-[rgba(14,91,42,0.08)] ${unread ? "shadow-sm" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 className="flex items-center gap-2 text-sm font-semibold text-[var(--ulendo-forest-dark)]">
                                {notification.title || "Notification"}
                                {severityLabel ? (
                                  <span className="rounded-full bg-[var(--ulendo-gold)]/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--ulendo-forest-dark)]">
                                    {severityLabel}
                                  </span>
                                ) : null}
                              </h4>
                              {notification.message ? (
                                <p className="mt-1 text-sm text-[var(--ulendo-text-muted)]">{notification.message}</p>
                              ) : null}
                            </div>
                            <span className="text-xs text-[var(--ulendo-text-muted)]">
                              {formatRelativeTime(notification.deliveredAt)}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-4 py-6 text-center text-[var(--ulendo-text-muted)]">No notifications</div>
                  )}
                </div>
                <div className="rounded-xl bg-[var(--ulendo-haze)]/90 px-3 py-2 text-center">
                  <button className="text-xs font-semibold uppercase tracking-wide text-[var(--ulendo-forest-dark)] hover:text-[var(--ulendo-forest)]">
                    Notifications dashboard
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => {
                setShowUserMenu((prev) => !prev);
                setShowNotifications(false);
                setShowSettingsMenu(false);
              }}
              className="flex items-center gap-2 rounded-xl border border-[rgba(14,91,42,0.16)] bg-white/90 px-2.5 py-1.5 text-left transition hover:border-[rgba(14,91,42,0.28)]"
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--ulendo-forest)] text-sm font-semibold text-[var(--ulendo-haze)]">
                  {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
                </span>
              )}
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-[var(--ulendo-forest-dark)]">{user?.name || "Officer"}</p>
                <p className="text-xs text-[var(--ulendo-text-muted)]">{user?.role || "Administrator"}</p>
              </div>
              <svg className="hidden h-4 w-4 text-[var(--ulendo-text-muted)] md:block" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            {showUserMenu && (
              <div className="absolute left-1/2 mt-3 w-56 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-[rgba(14,91,42,0.16)] bg-white/95 py-2 shadow-xl">
                <div className="px-4 py-3">
                  <p className="text-sm font-semibold text-[var(--ulendo-forest-dark)]">{user?.name || "Officer"}</p>
                  <p className="text-xs text-[var(--ulendo-text-muted)]">{user?.role || "Administrator"}</p>
                </div>
                <div className="border-t border-[rgba(14,91,42,0.12)]" />
                <button
                  onClick={() => {
                    setShowProfileSettings(true);
                    setShowUserMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--ulendo-text-muted)] transition hover:bg-[var(--ulendo-haze)]"
                >
                  Profile Settings
                </button>
                <button
                  onClick={() => {
                    setShowAccountPreferences(true);
                    setShowUserMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--ulendo-text-muted)] transition hover:bg-[var(--ulendo-haze)]"
                >
                  Account Preferences
                </button>
                <div className="border-t border-[rgba(14,91,42,0.12)] my-2" />
                <button
                  className="w-full px-4 py-2 text-left text-sm font-semibold text-red-500 transition hover:bg-red-50"
                  onClick={() => {
                    setShowUserMenu(false);
                    onSignOut?.();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
      {showOfflineGuidance ? (
        <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-2">
              <WifiOff className="mt-0.5 h-4 w-4" />
              <div>
                <p className="font-semibold">You are offline</p>
                <p className="text-sm">
                  Enable offline mode to keep capturing inspections and declarations until you reconnect.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOfflineModeEnabled(true)}
              className="inline-flex items-center justify-center rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-amber-500"
            >
              Enable offline mode
            </button>
          </div>
        </div>
      ) : null}

      {(showNotifications || showUserMenu || showSettingsMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowNotifications(false);
            setShowUserMenu(false);
            setShowSettingsMenu(false);
          }}
        />
      )}

      {typeof document !== "undefined"
        ? createPortal(
            <>
              <ProfileSettingsPanel
                open={showProfileSettings}
                onClose={() => setShowProfileSettings(false)}
                officerName={user?.name}
                officerRole={user?.role}
                officerEmail={user?.email}
                officerPhone={user?.phone}
                dutyStation={user?.dutyStation || pointOfEntry}
              />
              <AccountPreferencesPanel
                open={showAccountPreferences}
                onClose={() => setShowAccountPreferences(false)}
                officerEmail={user?.email}
              />
              <SyncIssuesModal
                open={showSyncIssues}
                onClose={() => setShowSyncIssues(false)}
                onChange={(next) => setSyncIssues(next)}
              />
            </>,
            document.body,
          )
        : null}
    </header>
  );
}
